import { Cookies } from 'react-cookie';
import config from '../config';
import * as constants from '../lib/constants';
import {
  getAnalysisLicenseStatus,
  licenseExceeded,
  convertBytes,
  licenseExceededItem,
  saveLocalStorage,
  loadLocalStorage,
} from '../lib/tools';
import { ActionTypes as TrashType } from '../actions/trash';

const {
  FULLSCREEN_MODE,
  TIMELINE_SCREEN_MODE,
  LICENSE_CAPACITY_STATUS,
  ROLE,
  REDIRECT_REASON,
  PAGE_PATH,
  PROVIDER,
} = constants;

const getUserAgent = () => {
  const navi = {
    ua: window.navigator.userAgent,
    platform: window.navigator.platform,
  };
  return navi;
};

const cookies = new Cookies();
const hasToken = (key = 'TOKEN') => !!cookies.get(key);
const clearToken = (key = 'TOKEN') => cookies.remove(key, { path: '/' });
const getApiToken = (key = 'TOKEN') => cookies.get(key);
const getTokenKey = guid => (guid && `TOKEN_${guid}`) || 'TOKEN';

const { project, locale, version } = config;
const appInitialState = {
  navi: getUserAgent(),
  isLogin: hasToken(),
  apiToken: getApiToken() || '',
  locale,
  project,
  menuIsOpen: false,
  userInfo: {
    operatorId: 0,
    name: '',
    companyName: '',
    iconUrl: '',
    companyCode: '',
    userId: '',
    role: ROLE.INITIAL,
    mapCenterSite: {},
  },
  isLoading: false,
  loadingMessages: ['loading'],
  logoutExec: false,
  redirectLoginPage: false,
  fullScreenMode: FULLSCREEN_MODE.dashboard,
  openUserInfo: false,
  version: {
    server: '',
    client: '',
  },
  license: {
    licenseExceededItems: [],
    user: { licenseExceeded: false },
    storage: { licenseExceeded: false, excessNumber: '' },
    remoteSupport: { enabled: false, licenseExceeded: false },
    ocr: { licenseStatus: 0, analyzable: false },
    voiceAnalysis: { licenseStatus: 0, analyzable: false },
  },
  compatibleInfo: {},
  displayDeviceList: true,
  displayTimelineList: false,
  deviceInformations: [],
  isOpenBlackboardList: false,
  supportTargetDevice: {},
  isMobile: false,
  provider: {
    name: '',
    pathName: '',
    isCansuke: false,
  },
  allowRemoteSupport: false,
  isForcusText: false,
  calendarData: {
    name: '',
  },
  stopApiCallTimer: false,
  timeLineScreenMode: TIMELINE_SCREEN_MODE.list,
  canLoadSearchCriteria: false,
  /** ユーザが参照可能な企業情報 */
  userCompanies: [],
  /** ユーザが参照可能なグループ情報 */
  userGroups: [],
  /** 現在選択している企業GUID */
  targetCompanyGuid: '',
  /** 現在選択している企業情報 */
  targetCompanyInfo: {},
  /** 現在選択しているグループID */
  targetGroupId: '',
  // latestGroupIds: [],
  /** 企業情報activate情報取得要求 */
  callCompanyActivated: false,
  /** グループ選択時はグループ情報＋userInfo.roleを加味したrole */
  innerRole: ROLE.INITIAL,
  /** 企業管理者かどうか */
  isCompanyAdmin: false,
  /** リダイレクトが必要かどうか */
  requireRedirect: false,
  /** リダイレクトに必要な情報 */
  redirectInfo: {
    reason: REDIRECT_REASON.default,
    path: '',
  },
  /** グループ情報等マスターデータ取得済み(再取得したい場合はfalseにする) */
  acquiredMaster: false,
};
const localStorageKey = config.localStorageKey.dashboard;

const getInnerRole = role => role;

export default (state = appInitialState, action) => {
  switch (action.type) {
    case 'DEFAULT': {
      const {
        logoutExec,
        redirectLoginPage,
        stopApiCallTimer,
        requireRedirect,
        redirectInfo,
        callCompanyActivated,
      } = appInitialState;
      return {
        ...state,
        logoutExec,
        redirectLoginPage,
        stopApiCallTimer,
        requireRedirect,
        redirectInfo,
        callCompanyActivated,
      };
    }
    // ローカルストレージ読み込み
    case 'LOAD_SEARCH_CRITERIA': {
      // 表示対象企業GUIDを読み込む
      const targetCompanyGuid =
        loadLocalStorage(localStorageKey.targetCompanyGuid) ||
        state.targetCompanyGuid;
      const targetCompanyInfo =
        state.userCompanies.find(
          company => company.companyGuid === targetCompanyGuid,
        ) || appInitialState.targetCompanyInfo;
      // 表示対象グループIDを読み込む
      const targetGroupId =
        loadLocalStorage(localStorageKey.targetGroupId) || state.targetGroupId;
      // 直近設定グループIDリストを読み込む
      // const loadedLatestGroupIds = loadLocalStorage(localStorageKey.latestGroupIds);
      // const latestGroupIds = (loadedLatestGroupIds && JSON.parse(loadedLatestGroupIds));
      // 内部用ロールを設定
      const innerRole = getInnerRole(state.userInfo.role);
      return {
        ...state,
        targetCompanyGuid,
        targetCompanyInfo,
        targetGroupId,
        // latestGroupIds,
        innerRole,
        isCompanyAdmin: state.userInfo.role === ROLE.COMPANY_ADMIN,
        canLoadSearchCriteria: true,
      };
    }
    case 'FINISH_LOAD_SEARCH_CRITERIA': {
      const { canLoadSearchCriteria } = appInitialState;
      return { ...state, canLoadSearchCriteria };
    }
    // 企業選択画面を表示するため表示対象企業をクリアする
    case 'CLEAR_TARGET_COMPANY': {
      const {
        targetCompanyGuid,
        targetCompanyInfo,
        targetGroupId,
        userGroups,
        // latestGroupIds, // 保留：ここでクリアする？
      } = appInitialState;
      // 表示対象企業更新
      saveLocalStorage(localStorageKey.targetCompanyGuid, targetCompanyGuid);
      // グループ情報を初期化
      saveLocalStorage(localStorageKey.targetGroupId, targetGroupId);
      // saveLocalStorage(localStorageKey.latestGroupIds, appInitialState.latestGroupIds);
      const innerRole = state.userInfo.role;
      return {
        ...state,
        targetCompanyGuid,
        targetCompanyInfo,
        userGroups,
        targetGroupId,
        // latestGroupIds,
        innerRole,
      };
    }
    // 表示対象企業を変更する
    case 'UPDATE_TARGET_COMPANY': {
      const {
        companyGuid: targetCompanyGuid = appInitialState.targetCompanyGuid,
      } = action;
      // 表示対象企業更新
      saveLocalStorage(localStorageKey.targetCompanyGuid, targetCompanyGuid);
      // グループ情報を初期化
      saveLocalStorage(
        localStorageKey.targetGroupId,
        appInitialState.targetGroupId,
      );
      // saveLocalStorage(localStorageKey.latestGroupIds, appInitialState.latestGroupIds);
      // TOKEN取得
      const tokenKey = getTokenKey(targetCompanyGuid);
      const apiToken = getApiToken(tokenKey) || '';

      return {
        ...state,
        // isLogin,
        apiToken,
        targetCompanyGuid,
        userGroups: appInitialState.userGroups,
        targetGroupId: appInitialState.targetGroupId,
        // latestGroupIds: appInitialState.latestGroupIds,
        callCompanyActivated: true,
        innerRole: appInitialState.innerRole,
        isCompanyAdmin: false,
        acquiredMaster: false,
      };
    }
    // 複数企業所属参加時の選択企業アクティベート状態返却処理
    case 'SUCCESS_API_GET_COMPANY_ACTIVATED': {
      const { isActivated } = action.payload;
      const requireRedirect = true;
      const redirectInfo = isActivated
        ? {
            reason: REDIRECT_REASON.selectCompanyActivated,
            path: PAGE_PATH.DS_ROOT,
          }
        : { reason: REDIRECT_REASON.default, path: PAGE_PATH.DS_ACTIVATE };
      return {
        ...state,
        requireRedirect,
        redirectInfo,
        callCompanyActivated: false,
      };
    }
    // 企業変更完了処理
    case 'COMPANY_CHANGED': {
      return { ...state, stopApiCallTimer: false };
    }
    // 表示対象グループを変更する
    case 'UPDATE_TARGET_GROUP': {
      const { groupId: targetGroupId = appInitialState.targetGroupId } = action;
      // グループIDをローカルストレージに保存する
      saveLocalStorage(localStorageKey.targetGroupId, targetGroupId);
      // if (targetGroupId !== appInitialState.targetGroupId) {
      //   // 設定したグループを上位に表示するためにグループIDをローカルストレージに保存する
      //   const loadedLatestGroupIds = loadLocalStorage(localStorageKey.latestGroupIds);
      //   const currentLatestGroupIds = (loadedLatestGroupIds && JSON.parse(loadedLatestGroupIds));
      //   currentLatestGroupIds.splice(0, 0, targetGroupId);
      //   if (LATEST_GROUP_IDS_NUM <= currentLatestGroupIds.length) {
      //     currentLatestGroupIds.splice(currentLatestGroupIds.length, 1);
      //   }
      //   const latestGroupIds = JSON.stringify(currentLatestGroupIds);
      //   saveLocalStorage(localStorageKey.latestGroupIds, latestGroupIds);
      // }
      const innerRole = getInnerRole(state.userInfo.role);
      const requireRedirect = !!targetGroupId;
      const redirectInfo = requireRedirect
        ? { reason: REDIRECT_REASON.groupAdmin, path: PAGE_PATH.DS_ROOT }
        : state.redirectInfo;
      return {
        ...state,
        targetGroupId,
        // latestGroupIds,
        innerRole,
        requireRedirect,
        redirectInfo,
        isCompanyAdmin: state.userInfo.role === ROLE.COMPANY_ADMIN,
        acquiredMaster: false,
      };
    }
    // ユーザ所属参加企業一覧を取得
    case 'SUCCESS_API_GET_USER_COMPANIES': {
      const { companies: userCompanies = [] } = action.payload;
      const { targetCompanyGuid: savedCompanyGuid } = state;
      // 保存済み表示対象企業が取得した企業一覧に存在するかどうか
      const existCompany = userCompanies.some(
        company => company.companyGuid === savedCompanyGuid,
      );
      // 取得した企業一覧が1つかどうか
      const singleCompany = userCompanies.length === 1;

      // 取得した所属参加企業一覧に設定済み表示対象企業が存在しない、または取得した企業が1件で表示対象企業が未設定の場合
      if (!existCompany || (singleCompany && !savedCompanyGuid)) {
        // 表示対象企業に、取得企業一覧が1件の場合はその企業を設定、それ以外の場合は初期値を設定
        const targetCompanyGuid = singleCompany
          ? userCompanies[0].companyGuid
          : appInitialState.targetCompanyGuid;
        const targetCompanyInfo =
          userCompanies.find(
            company => company.companyGuid === targetCompanyGuid,
          ) || appInitialState.targetCompanyInfo;
        // LocalStorageにを設定
        saveLocalStorage(localStorageKey.targetCompanyGuid, targetCompanyGuid);
        saveLocalStorage(
          localStorageKey.targetGroupId,
          appInitialState.targetGroupId,
        );
        // saveLocalStorage(localStorageKey.latestGroupIds, appInitialState.latestGroupIds);
        return {
          ...state,
          userCompanies,
          targetCompanyGuid,
          targetCompanyInfo,
          userGroups: appInitialState.userGroups,
          targetGroupId: appInitialState.targetGroupId,
          // latestGroupIds: appInitialState.latestGroupIds,
          innerRole: state.userInfo.role,
          isCompanyAdmin: state.userInfo.role === ROLE.COMPANY_ADMIN,
        };
      }
      // 取得したユーザ所属参加企業一覧を更新
      const targetCompanyInfo = userCompanies.find(
        company => company.companyGuid === savedCompanyGuid,
      );
      return { ...state, userCompanies, targetCompanyInfo };
    }
    // グループ一覧を取得
    case 'SUCCESS_API_GET_USER_GROUPS': {
      const { groups: userGroups = [] } = action.payload;
      const { targetGroupId: savedGroupId } = state;

      let targetGroupId;
      if (savedGroupId === '') {
        const defaultGroup = userGroups.find(group => group.isDefault);
        targetGroupId = defaultGroup ? defaultGroup.id : '';
        saveLocalStorage(localStorageKey.targetGroupId, targetGroupId);
        return {
          ...state,
          userGroups,
          targetGroupId,
        };
      }
      // 保存済み表示対象グループが取得したグループ一覧に存在するかどうか
      targetGroupId = userGroups.find(group => group.id === savedGroupId);

      // 取得したグループ一覧に設定済み表示対象グループが存在しない場合
      if (!targetGroupId) {
        // 保存済みグループID設定済みで、グループ一覧に存在しない場合は企業情報を初期化する
        const targetCompanyGuid =
          (savedGroupId &&
            !targetGroupId &&
            appInitialState.targetCompanyGuid) ||
          state.targetCompanyGuid;
        const targetCompanyInfo =
          (savedGroupId &&
            !targetGroupId &&
            appInitialState.targetCompanyInfo) ||
          state.targetCompanyInfo;
        // 取得したグループ一覧が1件の場合はそのグループ情報を設定
        targetGroupId = appInitialState;
        const innerRole = state.userInfo.role;
        // LocalStorageにを設定
        saveLocalStorage(localStorageKey.targetCompanyGuid, targetCompanyGuid);
        saveLocalStorage(localStorageKey.targetGroupId, targetGroupId);
        return {
          ...state,
          userGroups,
          targetCompanyGuid,
          targetCompanyInfo,
          targetGroupId,
          innerRole,
        };
      }
      // 取得したユーザ所属参加企業一覧を更新
      const innerRole = getInnerRole(state.userInfo.role);
      return {
        ...state,
        userGroups,
        innerRole,
        isCompanyAdmin: state.userInfo.role === ROLE.COMPANY_ADMIN,
      };
    }
    case 'SUCCESS_API_GET_OPERATORS': {
      const { operators = [] } = action.payload;
      const { fullScreenMode, userInfo } = state;
      // 企業管理者でオペレータ画面を表示しており、取得した作業者一覧が0件の場合はDSリダイレクトフラグをオンにする
      const requireRedirect =
        userInfo.role === ROLE.COMPANY_ADMIN &&
        fullScreenMode === FULLSCREEN_MODE.timelineTile &&
        operators.length === 0;
      const redirectInfo = requireRedirect
        ? { reason: REDIRECT_REASON.noUser, path: PAGE_PATH.DS_ROOT }
        : state.redirectInfo;
      return { ...state, requireRedirect, redirectInfo };
    }
    case 'REDIRECT_DASHBOARD': {
      const { requireRedirect, redirectInfo } = appInitialState;
      return { ...state, requireRedirect, redirectInfo };
    }
    case 'CLOSE_MENU': {
      return { ...state, menuIsOpen: false };
    }
    case 'TOGGLE_MENU': {
      return { ...state, menuIsOpen: !state.menuIsOpen };
    }
    case 'STOP_API_CALL_TIMER': {
      return { ...state, stopApiCallTimer: true };
    }
    case 'START_API_CALL_TIMER': {
      return { ...state, stopApiCallTimer: false };
    }
    case 'LOGOUT': {
      const {
        targetCompanyGuid,
        targetGroupId,
        // targetGroupIds,
        menuIsOpen,
        isLoading,
        redirectLoginPage,
        requireRedirect,
        redirectInfo,
        callCompanyActivated,
        stopApiCallTimer,
      } = appInitialState;
      // 表示対象企業更新
      saveLocalStorage(localStorageKey.targetCompanyGuid, targetCompanyGuid);
      // グループ情報を初期化
      saveLocalStorage(localStorageKey.targetGroupId, targetGroupId);
      // saveLocalStorage(localStorageKey.targetGroupIds, targetGroupIds);
      return {
        ...state,
        menuIsOpen,
        isLoading,
        logoutExec: true,
        redirectLoginPage,
        requireRedirect,
        redirectInfo,
        callCompanyActivated,
        stopApiCallTimer,
      };
    }
    case 'LOGOUT_DONE': {
      const targetCookies = cookies.getAll();
      Object.keys(targetCookies).forEach(token => {
        if (token.startsWith('TOKEN')) {
          clearToken(token);
        }
      });
      return {
        ...state,
        logoutExec: false,
        redirectLoginPage: true,
        isLogin: false,
      };
    }
    case 'OPEN_USER_PROMPT': {
      return { ...state, isLoading: false, loadingMessages: [] };
    }
    case 'OPEN_LOADING': {
      return { ...state, isLoading: true, loadingMessages: ['Loading...'] };
    }
    case 'CLOSE_LOADING': {
      return { ...state, isLoading: false, loadingMessages: [] };
    }
    case 'REQUEST_API_DOWNLOAD_MEDIA_FILES': {
      return {
        ...state,
        isLoading: true,
        loadingMessages: ['Loading...', '(zip archive)'],
      };
    }
    case 'SUCCESS_API_DOWNLOAD_MEDIA_FILES': {
      return { ...state, isLoading: false, loadingMessages: [] };
    }
    case 'REQUEST_API_REIMPORT_TAGS': {
      // TODO: i18nから読み込むように
      const message = 'タグをインポートしています。';
      return { ...state, isLoading: true, loadingMessages: [message] };
    }
    case 'SUCCESS_API_REIMPORT_TAGS': {
      return { ...state, isLoading: false, loadingMessages: [] };
    }
    case 'SUCCESS_API_GET_USER_INFO': {
      const userInfo = action.payload;
      const licenseExceededItems = licenseExceededItem(userInfo);
      const userLicenseExceeded = licenseExceeded(userInfo, 'userLicense');
      const storageLicenseExceeded = licenseExceeded(
        userInfo,
        'storageLicense',
      );
      const remoteSupportLicenseExceeded = licenseExceeded(
        userInfo,
        'remoteSupportLicense',
      );

      const user = { licenseExceeded: userLicenseExceeded };
      const storageLicenseInfo =
        (userInfo.companyLicenseInfo &&
          userInfo.companyLicenseInfo.storageLicenseInfo) ||
        {};
      const excessNumber =
        (storageLicenseInfo.excessNumber &&
          convertBytes(storageLicenseInfo.excessNumber)) ||
        '';
      const storage = { licenseExceeded: storageLicenseExceeded, excessNumber };
      const remoteSupportLicenseInfo =
        (userInfo.companyLicenseInfo &&
          userInfo.companyLicenseInfo.remoteSupportLicenseInfo) ||
        {};
      const remoteSupport = {
        enabled: remoteSupportLicenseInfo.enabled || false,
        licenseExceeded: remoteSupportLicenseExceeded,
      };
      const ocrLicenseInfo =
        (userInfo.companyLicenseInfo &&
          userInfo.companyLicenseInfo.ocrLicenseInfo) ||
        {};
      const ocrLicenseStatus = getAnalysisLicenseStatus(ocrLicenseInfo);
      const ocrAnalyzable = ocrLicenseStatus === LICENSE_CAPACITY_STATUS.ok;
      const ocr = {
        licenseStatus: ocrLicenseStatus,
        analyzable: ocrAnalyzable,
      };
      const voiceLicenseInfo =
        (userInfo.companyLicenseInfo &&
          userInfo.companyLicenseInfo.voiceAnalysisLicenseInfo) ||
        {};
      const voiceLicenseStatus = getAnalysisLicenseStatus(voiceLicenseInfo);
      const voiceAnalyzable = voiceLicenseStatus === LICENSE_CAPACITY_STATUS.ok;
      const voiceAnalysis = {
        licenseStatus: voiceLicenseStatus,
        analyzable: voiceAnalyzable,
      };
      const license = {
        licenseExceededItems,
        user,
        storage,
        remoteSupport,
        ocr,
        voiceAnalysis,
      };
      const role =
        typeof userInfo.role === 'string'
          ? Number(userInfo.role)
          : userInfo.role;
      const innerRole =
        state.innerRole === ROLE.INITIAL ? role : state.innerRole;
      return { ...state, userInfo: { ...userInfo, role }, license, innerRole };
    }
    case 'OPEN_TIMELINE': {
      const fullScreenMode = FULLSCREEN_MODE.timeline;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_TIMELINE_TILE': {
      const fullScreenMode = FULLSCREEN_MODE.timelineTile;
      return { ...state, fullScreenMode };
    }
    case TrashType.OPEN_TRASH: {
      const fullScreenMode = FULLSCREEN_MODE.trash;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_STAFF': {
      const fullScreenMode = FULLSCREEN_MODE.staff;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_COMPANY': {
      const fullScreenMode = FULLSCREEN_MODE.company;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_MAP': {
      const fullScreenMode = FULLSCREEN_MODE.map;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_DEVICE': {
      const fullScreenMode = FULLSCREEN_MODE.device;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_MASTER': {
      const fullScreenMode = FULLSCREEN_MODE.master;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_GROUP': {
      const fullScreenMode = FULLSCREEN_MODE.group;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_USER_INFO_DIALOG': {
      const openUserInfo = true;
      return { ...state, openUserInfo };
    }
    case 'OPEN_ACTION_LOG': {
      const fullScreenMode = FULLSCREEN_MODE.actionLog;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_REPORT': {
      const fullScreenMode = FULLSCREEN_MODE.report;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_REPORT_EDIT_PAGE': {
      const fullScreenMode = FULLSCREEN_MODE.reportEdit;
      return { ...state, fullScreenMode };
    }
    case 'OPEN_ACCESS_CONTROL': {
      const fullScreenMode = FULLSCREEN_MODE.accessControl;
      return { ...state, fullScreenMode };
    }
    case 'CLOSE_USER_INFO_DIALOG': {
      const openUserInfo = false;
      return { ...state, openUserInfo };
    }
    case 'CLOSE_FULLSCREEN': {
      const fullScreenMode = FULLSCREEN_MODE.dashboard;
      const timeLineScreenMode = TIMELINE_SCREEN_MODE.list;
      return { ...state, fullScreenMode, timeLineScreenMode };
    }
    case 'SUCCESS_API_APP_RELEASE_NUMBER': {
      const server = action.payload.releaseNumber || '';
      const newVersion = { client: version, server };
      return { ...state, version: newVersion };
    }
    case 'UPDATE_PROVIDER': {
      const { provider } = appInitialState;
      const { providerName } = action;
      provider.name = providerName;
      provider.pathName = `/${providerName}`;
      provider.isCansuke = providerName.includes(PROVIDER.cansuke);
      return { ...state, provider };
    }
    case 'UPDATE_COMPATIBLE_INFO': {
      const result = action.compatibleInfo;
      const isPC =
        state.compatibleInfo.isPC === undefined
          ? result.isPC
          : state.compatibleInfo.isPC;
      const isAndroid =
        state.compatibleInfo.isAndroid === undefined
          ? result.isAndroid
          : state.compatibleInfo.isAndroid;
      const compatibleInfo = { ...result, isPC };
      return { ...state, compatibleInfo, isMobile: !isPC && isAndroid };
    }
    case 'TOGGLE_MOBILE_DEVICE_LIST': {
      const displayDeviceList = !state.displayDeviceList;
      const displayTimelineList = !displayDeviceList;
      return { ...state, displayDeviceList, displayTimelineList };
    }
    case 'TOGGLE_MOBILE_TIMELINE_LIST': {
      const displayTimelineList = !state.displayTimelineList;
      const displayDeviceList = !displayTimelineList;
      return { ...state, displayDeviceList, displayTimelineList };
    }
    case 'SUCCESS_API_GET_USER_DEVICES': {
      const deviceInformations =
        (action.payload && action.payload.deviceInformations) || [];
      if (!state.isOpenBlackboardList) {
        return { ...state, deviceInformations };
      }
      // 黒板一覧表示中は選択デバイス情報更新も行う
      const { deviceId } = state.supportTargetDevice;
      const supportTargetDevice =
        deviceInformations.find(device => device.deviceId === deviceId) || {};
      return { ...state, deviceInformations, supportTargetDevice };
    }
    case 'OPEN_BLACKBOARD_LIST_DIALOG': {
      const { deviceId } = action;
      const { deviceInformations } = state;
      const isOpenBlackboardList = true;
      const supportTargetDevice =
        deviceInformations.find(device => device.deviceId === deviceId) || {};
      return { ...state, isOpenBlackboardList, supportTargetDevice };
    }
    case 'CLOSE_BLACKBOARD_LIST_DIALOG': {
      const isOpenBlackboardList = false;
      return { ...state, isOpenBlackboardList, supportTargetDevice: {} };
    }
    case 'SET_ALLOW_REMOTE_SUPPORT': {
      const { allowRemoteSupport } = action;
      return { ...state, allowRemoteSupport };
    }
    case 'SET_FORCUS_TEXT': {
      const { isForcusText } = action;
      return { ...state, isForcusText };
    }
    case 'OPEN_CALENDAR': {
      const { name } = action;
      const calendarData = { ...state.calendarData, name };
      return { ...state, calendarData };
    }
    case 'CLOSE_CALENDAR': {
      const { calendarData } = appInitialState;
      return { ...state, calendarData };
    }
    case 'SWITCH_TIMELINE_SCREEN_MODE': {
      const timeLineScreenMode =
        state.timeLineScreenMode === TIMELINE_SCREEN_MODE.list
          ? TIMELINE_SCREEN_MODE.grid
          : TIMELINE_SCREEN_MODE.list;
      return { ...state, timeLineScreenMode };
    }
    case 'UPDATE_ACQUIRED_MASTER': {
      const { value: acquiredMaster } = action;
      return { ...state, acquiredMaster };
    }
    default:
      return state;
  }
};
